/**
 * MI Max Setpoint Calculator
 *
 * Mirrors Shell's "MI Max Setpoint Calculator" spreadsheet as an inline safety
 * check on the Add/Edit Forced-On Log modal. It confirms that increasing an EHT
 * maintain setpoint will not push the heater's sheath temperature past the
 * hazardous-area temperature rating (a sheath-temperature violation).
 *
 * Source-of-truth is spreadsheet cell B31:
 *   result = IF( UpperLimit > (HighestSheathTemp + (NewSetpoint - OriginalSetpoint) + SafetyFactor), "YES", "NO" )
 *
 * - HighestSheathTemp comes from the device `max_sheath_temp`, in °C.
 * - OriginalSetpoint / NewSetpoint are the free-text setpoint fields on the form.
 * - SafetyFactor is the facility preference `safetyFactor` (default 40).
 * - UpperLimit (= Area Temp Rating) is derived from the device `auto_ignition_temp`
 *   (AIT) via the T-class legend below (the ticket: "AIT drives the Area
 *   Temperature Rating / T-class"). No separate variance is applied.
 *
 * Device temps (`max_sheath_temp`, `auto_ignition_temp`) are stored and read in
 * Celsius. The setpoint fields, however, are entered by the operator in their
 * preferred unit (`temperatureUnit`, 'C' | 'F'). When that preference is 'F',
 * the setpoints are converted to Celsius here before the math runs, so every
 * operand in the formula is Celsius.
 */

import { fahrenheitToCelsiusConvertor, toFiniteNumber } from 'src/utils/utils';

/**
 * Hazardous-area temperature classification legend (°C), from the ticket / ISO.
 * The Area Temperature Rating is the highest of these values that is still
 * strictly below the gas Auto-Ignition Temperature (standard IEC/NEC rule:
 * the equipment temperature class must sit below the AIT).
 */
export const T_CLASS_RATINGS: Readonly<Record<string, number>> = {
  T1: 450,
  T2: 300,
  T2A: 280,
  T2B: 260,
  T2C: 230,
  T2D: 215,
  T3A: 180,
  T3B: 165,
  T3C: 160,
  T4: 135,
  T4A: 120,
  T5: 100,
  T6: 85,
};

/** Lowest T-class rating (°C) — T6 = 85. Below this, no Area Temp Rating exists. */
export const LOWEST_T_CLASS_C = Math.min(...Object.values(T_CLASS_RATINGS));

export type SafetyCheckStatus = 'pass' | 'fail' | 'incomplete';

export interface SafetyCheckInput {
  /** device `max_sheath_temp` (°C) */
  maxSheathTemp?: number | null;
  /** device `auto_ignition_temp` (°C) — drives the Area Temp Rating */
  autoIgnitionTemp?: number | null;
  /** form field `originalSetPoint` (free-text string) */
  originalSetPoint?: string | number | null;
  /** form field `forcedOnSetPoint` — the new requested setpoint (free-text string) */
  forcedOnSetPoint?: string | number | null;
  /** facility preference `safetyFactor` (default 40) */
  safetyFactor?: number | null;
  /**
   * The operator's temperature-unit preference ('C' | 'F', default 'C'). The
   * setpoint fields are entered in this unit; when 'F' they are converted to
   * Celsius before the math so every operand matches the Celsius device temps.
   */
  temperatureUnit?: string | null;
}

export interface SafetyCheckResult {
  status: SafetyCheckStatus;
  /** i18n keys for the fields that were missing / unparseable (only when incomplete) */
  missingFields: string[];
  /**
   * True when the AIT is set but sits at/below the lowest temperature class
   * (85 °C), so no Area Temp Rating can be derived — a distinct, unsafe case
   * rather than a missing field. Only meaningful when status is 'fail'.
   */
  aitBelowLowestClass?: boolean;
  /** operands echoed back so the UI tooltip can render the breakdown without recomputing */
  projectedSheathTemp?: number;
  areaTempRating?: number;
  maxSheathTemp?: number;
  autoIgnitionTemp?: number;
  originalSetPoint?: number;
  forcedOnSetPoint?: number;
  safetyFactor?: number;
}

/**
 * Derive the Area Temperature Rating from a gas Auto-Ignition Temperature.
 * Returns the highest T-class rating strictly below the AIT, or null when the
 * AIT is missing, non-numeric, or below the lowest class (85 °C).
 *
 * e.g. AIT 425 -> 300 (T2), matching the Shell spreadsheet's worked example.
 */
export function deriveAreaTempRating (autoIgnitionTemp?: number | null): number | null {
  if (autoIgnitionTemp === null || autoIgnitionTemp === undefined || !Number.isFinite(autoIgnitionTemp)) {
    return null;
  }
  const below = Object.values(T_CLASS_RATINGS).filter((rating) => rating < autoIgnitionTemp);
  return below.length > 0 ? Math.max(...below) : null;
}

/**
 * Evaluate whether a maintain-setpoint change is within safe sheath-temperature
 * limits, per spreadsheet cell B31.
 *
 * Returns `incomplete` (with the offending i18n field keys) when any required
 * input is missing or unparseable, so the caller can prompt for the gaps rather
 * than showing a spurious pass/fail.
 */
export function evaluateSetpointSafety (input: SafetyCheckInput): SafetyCheckResult {
  const safetyFactor = toFiniteNumber(input.safetyFactor) ?? 40;

  const maxSheathTemp = toFiniteNumber(input.maxSheathTemp);
  const autoIgnitionTemp = toFiniteNumber(input.autoIgnitionTemp);
  const areaTempRating = deriveAreaTempRating(autoIgnitionTemp);

  // Setpoints are entered in the operator's preferred unit. Convert F -> C so
  // every operand in the formula (and the echoed-back tooltip breakdown) is
  // Celsius, matching the device temps and the derived Area Temp Rating.
  const toCelsius = (value: number | null): number | null =>
    value === null || input.temperatureUnit !== 'F' ? value : fahrenheitToCelsiusConvertor(value);
  const originalSetPoint = toCelsius(toFiniteNumber(input.originalSetPoint));
  const forcedOnSetPoint = toCelsius(toFiniteNumber(input.forcedOnSetPoint));

  // Truly-empty / unparseable inputs -> prompt the operator to fill them in.
  // Note: AIT is only "missing" when it has no value; an AIT that is set but
  // below the lowest temperature class is handled separately below.
  const missingFields: string[] = [];
  if (maxSheathTemp === null) missingFields.push('maxSheathTemp');
  if (autoIgnitionTemp === null) missingFields.push('ait');
  if (originalSetPoint === null) missingFields.push('forcedOn.originalSetPoint');
  if (forcedOnSetPoint === null) missingFields.push('forcedOn.setPoint');

  if (missingFields.length > 0) {
    return { status: 'incomplete', missingFields };
  }

  // AIT is set but below the lowest temperature class (85 °C): no rating can be
  // derived, so no setpoint is provably safe. Distinct, unsafe (fail) case.
  if (areaTempRating === null) {
    return {
      status: 'fail',
      missingFields: [],
      aitBelowLowestClass: true,
      autoIgnitionTemp: autoIgnitionTemp ?? undefined,
      maxSheathTemp: maxSheathTemp ?? undefined,
      originalSetPoint: originalSetPoint ?? undefined,
      forcedOnSetPoint: forcedOnSetPoint ?? undefined,
      safetyFactor,
    };
  }

  const projectedSheathTemp = maxSheathTemp + (forcedOnSetPoint - originalSetPoint) + safetyFactor;
  const status: SafetyCheckStatus = areaTempRating > projectedSheathTemp ? 'pass' : 'fail';

  return {
    status,
    missingFields: [],
    projectedSheathTemp,
    areaTempRating,
    maxSheathTemp,
    autoIgnitionTemp: autoIgnitionTemp ?? undefined,
    originalSetPoint,
    forcedOnSetPoint,
    safetyFactor,
  };
}
